import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { isOverlayAllowed, getEntityRoute } from '../config/OverlayRegistry';

/**
 * OverlayContext
 *
 * Manages overlay state derived ONLY from URL search params.
 * No duplicated local state - URL is the single source of truth.
 *
 * URL format: ?overlay=entity&id=123&background=/current-path
 *
 * The `background` param stores the location that was active when
 * the overlay was opened, so we can restore it on close.
 */

const OverlayContext = createContext(null);

export const useOverlay = () => {
  const context = useContext(OverlayContext);
  if (!context) {
    throw new Error('useOverlay must be used within an OverlayProvider');
  }
  return context;
};

export const OverlayProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const previousUrlRef = useRef(null);

  // Derive overlay state from URL (single source of truth)
  const overlayEntity = searchParams.get('overlay');
  const overlayId = searchParams.get('id');
  const background = searchParams.get('background');
  const isOpen = Boolean(overlayEntity && overlayId);

  // Store URL without overlay params when overlay opens
  useEffect(() => {
    if (isOpen && previousUrlRef.current === null) {
      // Store the current path without overlay params for browser back
      previousUrlRef.current = location.pathname;
    } else if (!isOpen) {
      previousUrlRef.current = null;
    }
  }, [isOpen, location.pathname]);

  /**
   * Build current location string (pathname + non-overlay search params)
   * This captures where the user is before opening the overlay.
   */
  const getCurrentLocationString = useCallback(() => {
    // Get all current params except overlay-related ones
    const currentParams = new URLSearchParams(searchParams);
    currentParams.delete('overlay');
    currentParams.delete('id');
    currentParams.delete('background');

    const paramString = currentParams.toString();
    return paramString ? `${location.pathname}?${paramString}` : location.pathname;
  }, [location.pathname, searchParams]);

  /**
   * Open overlay for an entity
   * @param {string} entity - Entity type (e.g., 'item', 'vendor')
   * @param {string|number} id - Entity ID
   */
  const openOverlay = useCallback((entity, id) => {
    if (!entity || !id) {
      console.warn('openOverlay requires entity and id');
      return;
    }

    // Check if overlay is allowed for this entity
    if (!isOverlayAllowed(entity)) {
      // Navigate to full page instead
      const route = getEntityRoute(entity, id);
      navigate(route);
      return;
    }

    // Capture current location before opening overlay
    const currentLocation = getCurrentLocationString();

    // Update URL with overlay params including background
    const newParams = new URLSearchParams(searchParams);
    newParams.set('overlay', entity);
    newParams.set('id', String(id));
    newParams.set('background', currentLocation);
    // Carry location.state across so pages that depend on it (e.g. SO create from a
    // Quotation) don't lose it - and remount - when a drawer is opened over them.
    setSearchParams(newParams, { replace: false, state: location.state });
  }, [searchParams, setSearchParams, getCurrentLocationString, navigate, location.state]);

  /**
   * Close overlay and restore previous screen
   * @param {'x'|'back'|'esc'|'navigation'} [reason='x'] - Close reason for analytics
   */
  const closeOverlay = useCallback((reason = 'x') => {
    if (!isOpen) return;

    // Optional: Log close reason for analytics/debugging
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[Overlay] Closed with reason: ${reason}`);
    }

    if (background) {
      // Navigate back to the captured background location
      navigate(background, { replace: true, state: location.state });
    } else {
      // No background - just remove overlay params from current URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('overlay');
      newParams.delete('id');
      newParams.delete('background');

      const paramString = newParams.toString();
      const newPath = paramString ? `${location.pathname}?${paramString}` : location.pathname;
      navigate(newPath, { replace: true, state: location.state });
    }
  }, [isOpen, background, searchParams, location.pathname, location.state, navigate]);

  /**
   * Handle browser back button
   * When overlay is open and user presses back, close overlay instead of navigating
   */
  useEffect(() => {
    const handlePopState = () => {
      // Check if overlay was open before this popstate
      const params = new URLSearchParams(window.location.search);
      const stillHasOverlay = params.get('overlay') && params.get('id');

      if (!stillHasOverlay && previousUrlRef.current) {
        // Overlay was closed by back button
        if (process.env.NODE_ENV === 'development') {
          console.debug('[Overlay] Closed via browser back');
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  /**
   * Handle Esc key to close overlay
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        closeOverlay('esc');
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, closeOverlay]);

  const value = {
    // State (derived from URL)
    isOpen,
    entity: overlayEntity,
    id: overlayId,
    background, // The location to restore on close

    // Actions
    openOverlay,
    closeOverlay,
  };

  return (
    <OverlayContext.Provider value={value}>
      {children}
    </OverlayContext.Provider>
  );
};

export default OverlayContext;
